import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Comment Document ---

export interface CommentAttachment {
  file_id: string;
  file_name: string;
  file_size: number;
  content_type: string;
}

export interface Comment {
  id: string;
  tenant_id: string;
  entity_type: string;
  entity_id: string;
  author_id: string;
  content: string;
  is_internal: boolean;
  parent_id: string | null;
  mentions: string[];
  attachments: CommentAttachment[];
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Team Document ---

export interface TeamMember {
  user_id: string;
  role: string;
  joined_at: string;
}

export interface Team {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  owner_id: string;
  members: TeamMember[];
  is_active: boolean;
  visibility: string;
  tags: string[];
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- SharedView Document ---

export interface SharedView {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  module: string;
  owner_id: string;
  columns: string[];
  filters: Record<string, string>;
  sort_field: string | null;
  sort_descending: boolean;
  page_size: number;
  is_default: boolean;
  is_shared: boolean;
  shared_with_users: string[];
  shared_with_roles: string[];
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Create/Update DTOs ---

export interface CreateCommentDto {
  entity_type: string;
  entity_id: string;
  content: string;
  is_internal?: boolean;
  parent_id?: string;
  mentions?: string[];
  attachments?: CommentAttachment[];
}

export interface UpdateCommentDto {
  content: string;
  is_internal?: boolean;
  mentions?: string[];
  attachments?: CommentAttachment[];
}

export interface CreateTeamDto {
  name: string;
  description?: string;
  visibility?: string;
  members?: { user_id: string; role?: string }[];
  tags?: string[];
}

export interface UpdateTeamDto {
  name?: string;
  description?: string;
  visibility?: string;
  is_active?: boolean;
  tags?: string[];
}

export interface TeamMemberDto {
  user_id: string;
  role?: string;
}

export interface CreateSharedViewDto {
  name: string;
  description?: string;
  module: string;
  columns?: string[];
  filters?: Record<string, string>;
  sort_field?: string;
  sort_descending?: boolean;
  page_size?: number;
  is_default?: boolean;
  is_shared?: boolean;
  shared_with_users?: string[];
  shared_with_roles?: string[];
}

export interface UpdateSharedViewDto {
  name?: string;
  description?: string;
  columns?: string[];
  filters?: Record<string, string>;
  sort_field?: string;
  sort_descending?: boolean;
  page_size?: number;
  is_default?: boolean;
  is_shared?: boolean;
  shared_with_users?: string[];
  shared_with_roles?: string[];
}

// --- Constants ---

export const COMMENT_ENTITY_TYPES = [
  'customer', 'contact', 'opportunity', 'ticket', 'order', 'quote', 'lead', 'campaign',
] as const;

export const TEAM_VISIBILITY = ['public', 'private'] as const;
export const TEAM_MEMBER_ROLES = ['owner', 'admin', 'member'] as const;

export const SHARED_VIEW_MODULES = [
  'customers', 'contacts', 'opportunities', 'tickets', 'orders',
  'quotes', 'leads', 'campaigns', 'products', 'activities',
] as const;

// --- Comment API ---

export function getComments(
  params: SearchParams & { entity_type: string; entity_id: string; author_id?: string; is_internal?: boolean; parent_id?: string },
) {
  return apiGet<ApiResponse<PagedResponse<Comment>>>('/api/v1/comments', { params });
}

export function getCommentById(id: string) {
  return apiGet<ApiResponse<Comment>>(`/api/v1/comments/${id}`);
}

export function getCommentCount(entity_type: string, entity_id: string) {
  return apiGet<ApiResponse<{ count: number }>>('/api/v1/comments/count', {
    params: { entity_type, entity_id },
  });
}

export function createComment(data: CreateCommentDto) {
  return apiPost<ApiResponse<Comment>>('/api/v1/comments', data);
}

export function updateComment(id: string, data: UpdateCommentDto) {
  return apiPut<ApiResponse<Comment>>(`/api/v1/comments/${id}`, data);
}

export function deleteComment(id: string) {
  return apiDelete<ApiResponse<null>>(`/api/v1/comments/${id}`);
}

// --- Team API ---

export function getTeams(params?: SearchParams & { visibility?: string; is_active?: boolean }) {
  return apiGet<ApiResponse<PagedResponse<Team>>>('/api/v1/teams', { params });
}

export function getTeamById(id: string) {
  return apiGet<ApiResponse<Team>>(`/api/v1/teams/${id}`);
}

export function getMyTeams(params?: { from?: number; size?: number }) {
  return apiGet<ApiResponse<PagedResponse<Team>>>('/api/v1/teams/my', { params });
}

export function createTeam(data: CreateTeamDto) {
  return apiPost<ApiResponse<Team>>('/api/v1/teams', data);
}

export function updateTeam(id: string, data: UpdateTeamDto) {
  return apiPut<ApiResponse<Team>>(`/api/v1/teams/${id}`, data);
}

export function addTeamMember(teamId: string, data: TeamMemberDto) {
  return apiPost<ApiResponse<Team>>(`/api/v1/teams/${teamId}/members`, data);
}

export function removeTeamMember(teamId: string, userId: string) {
  return apiDelete<ApiResponse<Team>>(`/api/v1/teams/${teamId}/members/${userId}`);
}

export function deleteTeam(id: string) {
  return apiDelete<ApiResponse<null>>(`/api/v1/teams/${id}`);
}

// --- SharedView API ---

export function getSharedViews(params?: SearchParams & { module?: string; owner_id?: string; is_shared?: boolean }) {
  return apiGet<ApiResponse<PagedResponse<SharedView>>>('/api/v1/shared-views', { params });
}

export function getAvailableViews(module: string) {
  return apiGet<ApiResponse<SharedView[]>>('/api/v1/shared-views/available', {
    params: { module },
  });
}

export function getSharedViewById(id: string) {
  return apiGet<ApiResponse<SharedView>>(`/api/v1/shared-views/${id}`);
}

export function createSharedView(data: CreateSharedViewDto) {
  return apiPost<ApiResponse<SharedView>>('/api/v1/shared-views', data);
}

export function updateSharedView(id: string, data: UpdateSharedViewDto) {
  return apiPut<ApiResponse<SharedView>>(`/api/v1/shared-views/${id}`, data);
}

export function deleteSharedView(id: string) {
  return apiDelete<ApiResponse<null>>(`/api/v1/shared-views/${id}`);
}
